import { useState } from "react";
import { Box, CircularProgress, Collapse, Divider, Paper, Stack, Typography, alpha } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Task } from "@/types";
import { TaskListItem } from "@/resources/tasks/components/task-list/TaskListItem";
import { BORDER_RADIUS } from "@/constants/layout";
import { useAppTheme } from "@/hooks/useAppTheme";

interface UrgencySection {
  key: string;
  label: string;
  tasks: Task[];
  accentColor: string;
  accentColorDark: string;
}

interface TaskQueueProps {
  overdueTasks: Task[];
  todayTasks: Task[];
  upcomingTasks: Task[];
  laterTasks: Task[];
  loading?: boolean;
  onTaskClick: (task: Task) => void;
}

export const TaskQueue = ({
  overdueTasks,
  todayTasks,
  upcomingTasks,
  laterTasks,
  loading = false,
  onTaskClick,
}: TaskQueueProps) => {
  const { theme, isDarkMode } = useAppTheme();

  // Smart expand: only auto-expand the highest-priority non-empty groups.
  // Overdue + Today are always expanded if they have items.
  // This Week expands only if there's nothing more urgent.
  // Later is always collapsed by default.
  const hasOverdue = overdueTasks.length > 0;
  const hasToday = todayTasks.length > 0;
  const hasUrgent = hasOverdue || hasToday;

  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    overdue: true,
    today: true,
    upcoming: !hasUrgent, // auto-expand only when nothing more urgent
    later: false,
  });

  const toggleSection = (key: string) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const totalTasks = overdueTasks.length + todayTasks.length + upcomingTasks.length + laterTasks.length;

  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{ border: 1, borderColor: "divider", borderRadius: BORDER_RADIUS.XL, overflow: "hidden" }}
      >
        <Box display="flex" justifyContent="center" py={6}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Loading tasks...
            </Typography>
          </Stack>
        </Box>
      </Paper>
    );
  }

  if (totalTasks === 0) {
    return (
      <Paper
        elevation={0}
        sx={{ border: 1, borderColor: "divider", borderRadius: BORDER_RADIUS.XL, overflow: "hidden" }}
      >
        {/* Header row — matches CompactTable pattern */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 0.75,
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "action.hover",
          }}
        >
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Task
          </Typography>
          <Stack direction="row" spacing={3}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Due
            </Typography>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Status
            </Typography>
          </Stack>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 5 }}>
          <Typography variant="body2" color="text.secondary">
            No tasks assigned to you
          </Typography>
        </Box>
      </Paper>
    );
  }

  const sections: UrgencySection[] = [
    { key: "overdue", label: "Overdue", tasks: overdueTasks, accentColor: "#DC2626", accentColorDark: "#F87171" },
    { key: "today", label: "Today", tasks: todayTasks, accentColor: "#D97706", accentColorDark: "#FBBF24" },
    {
      key: "upcoming",
      label: "This Week",
      tasks: upcomingTasks,
      accentColor: theme.palette.primary.main,
      accentColorDark: theme.palette.primary.light,
    },
    {
      key: "later",
      label: "Later",
      tasks: laterTasks,
      accentColor: theme.palette.divider,
      accentColorDark: theme.palette.divider,
    },
  ];

  const visibleSections = sections.filter((s) => s.tasks.length > 0);

  return (
    <Paper
      elevation={0}
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: BORDER_RADIUS.XL,
        overflow: "hidden",
        maxHeight: 600,
        overflowY: "auto",
      }}
    >
      {visibleSections.map((section, sectionIdx) => {
        const accent =
          section.key === "later" ? "text.disabled" : isDarkMode ? section.accentColorDark : section.accentColor;
        const accentRaw =
          section.key === "later" ? theme.palette.divider : isDarkMode ? section.accentColorDark : section.accentColor;
        const isExpanded = expanded[section.key] ?? false;

        return (
          <Box key={section.key}>
            {/* Section header — clickable to toggle */}
            <Box
              onClick={() => toggleSection(section.key)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2.5,
                py: 1.25,
                borderLeft: `3px solid ${accentRaw}`,
                bgcolor: isDarkMode ? alpha(accentRaw, 0.04) : alpha(accentRaw, 0.03),
                borderBottom: "1px solid",
                borderColor: "divider",
                cursor: "pointer",
                userSelect: "none",
                "&:hover": { bgcolor: isDarkMode ? alpha(accentRaw, 0.08) : alpha(accentRaw, 0.06) },
                ...(sectionIdx > 0 && { borderTop: "1px solid", borderTopColor: "divider" }),
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography
                  variant="overline"
                  sx={{ fontWeight: 700, letterSpacing: "0.08em", color: accent, lineHeight: 1 }}
                >
                  {section.label}
                </Typography>
                <Typography variant="caption" color="text.disabled" fontWeight={500}>
                  {section.tasks.length}
                </Typography>
              </Stack>
              {isExpanded ? (
                <ExpandLessIcon sx={{ fontSize: 18, color: "text.disabled" }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: 18, color: "text.disabled" }} />
              )}
            </Box>

            {/* Task items — collapsible */}
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Stack divider={<Divider />}>
                {section.tasks.map((task) => (
                  <TaskListItem
                    key={task.id}
                    task={task}
                    showAssignedUser={false}
                    showRelatedTo
                    onTaskClick={onTaskClick}
                  />
                ))}
              </Stack>
            </Collapse>
          </Box>
        );
      })}
    </Paper>
  );
};
